import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attemptCount = 0) => {
    if (attemptCount >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setPaymentInfo(data);
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }
      
      // Continue polling
      setAttempts(attemptCount + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attemptCount + 1), 2000);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a0f0a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'rgba(212, 175, 55, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.3)',
        borderRadius: '20px',
        padding: '48px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center'
      }}>
        {status === 'checking' && (
          <>
            <Loader 
              size={64} 
              style={{ 
                color: '#D4AF37', 
                margin: '0 auto 24px',
                animation: 'spin 1s linear infinite'
              }} 
            />
            <h2 style={{
              fontSize: '28px',
              fontFamily: 'Cinzel, serif',
              color: '#D4AF37',
              marginBottom: '16px'
            }}>
              Processing Payment
            </h2>
            <p style={{ color: '#E6D7C3', lineHeight: '1.6' }}>
              Please wait while we confirm your payment...
              <br />
              <span style={{ fontSize: '14px', color: '#A57C33' }}>
                Attempt {attempts + 1} of {maxAttempts}
              </span>
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle 
              size={64} 
              style={{ 
                color: '#2ed573', 
                margin: '0 auto 24px'
              }} 
            />
            <h2 style={{
              fontSize: '32px',
              fontFamily: 'Cinzel, serif',
              color: '#2ed573',
              marginBottom: '16px'
            }}>
              Payment Successful!
            </h2>
            <p style={{ color: '#E6D7C3', marginBottom: '24px', lineHeight: '1.6' }}>
              Your payment has been processed successfully.
              {paymentInfo && (
                <>
                  <br />
                  <span style={{ fontSize: '14px', color: '#A57C33' }}>
                    Amount: ${paymentInfo.amount} {paymentInfo.currency.toUpperCase()}
                  </span>
                </>
              )}
            </p>
            
            <button
              onClick={() => navigate('/profile')}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8941E 100%)',
                border: 'none',
                borderRadius: '12px',
                color: '#1a1a1a',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                fontFamily: 'Cinzel, serif'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Return to Profile
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle 
              size={64} 
              style={{ 
                color: '#ff4757', 
                margin: '0 auto 24px'
              }} 
            />
            <h2 style={{
              fontSize: '28px',
              fontFamily: 'Cinzel, serif',
              color: '#ff4757',
              marginBottom: '16px'
            }}>
              Payment Verification Failed
            </h2>
            <p style={{ color: '#E6D7C3', marginBottom: '24px', lineHeight: '1.6' }}>
              We couldn't verify your payment. Please check your email for confirmation or contact support.
            </p>
            
            <button
              onClick={() => navigate('/profile')}
              style={{
                width: '100%',
                padding: '16px',
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '12px',
                color: '#D4AF37',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontFamily: 'Cinzel, serif'
              }}
            >
              Return to Profile
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccessPage;
